import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../config/api';

const SettingsContext = createContext();

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState({
        siteName: 'Ankara Merkez Far',
        siteDescription: "Ankara'nın en güvenilir far ve stop tamir servisi.",
        siteKeywords: 'Ankara far tamircisi, far camı değişimi, stop lambası tamiri',
        siteAuthor: 'Ankara Merkez Far',
        googleAnalytics: '',
        logoUrl: '/logo.jpg',
        phone: '05523067171',
        phone2: '05050100671',
        whatsapp: '905523067171',
        email: 'merkezfar@gmail.com',
        address: '568. Sk. No:6 Altın Örnek Sanayi Sitesi İvedik Osb.',
        city: 'Ankara',
        facebook: '',
        instagram: '',
        twitter: '',
        youtube: '',
        linkedin: '',
        workingHours: 'Pazartesi - Cumartesi: 09:00 - 19:00',
        heroTitle: 'Uzman Far, Stop Tamiri ve Temizliği',
        heroSubtitle: 'İçi kararan, güneşten solan ve üzeri çizilmiş otomobil farlarınızı yeni gibi yapıyoruz.',
        heroImage: '/logo.jpg',
        aboutTitle: 'Ankara Merkez Far',
        aboutDescription: '15 yılı aşkın deneyimimizle far ve stop tamir hizmetlerinde öncü firmayız.',
        aboutContent: 'Profesyonel ekibimizle hizmetinizdeyiz.',
        aboutImage: '/logo.jpg',
        ctaTitle: 'Hemen Bizimle İletişime Geçin',
        ctaDescription: 'Araçlarınız için profesyonel far ve stop tamiri hizmeti.',
        ctaButtonText: 'İletişime Geç',
        ctaButtonUrl: '/iletisim',
        mapEmbed: '',
        experienceYears: '15+',
        happyCustomers: '5000+',
        satisfaction: '100%',
        servicesCount: '7'
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            setError(null);

            // Always use real API
            const response = await fetch(`${API_URL}/settings?_=${Date.now()}`, {
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setSettings(prev => ({ ...prev, ...data }));
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            console.error('Error fetching settings:', err);
            setError('Veritabanına bağlanırken hata oluştu: ' + err.message);
            // Keep existing settings on error
        } finally {
            setLoading(false);
        }
    };

    const updateSettings = async (newSettings) => {
        const token = localStorage.getItem('adminToken');

        if (!token) {
            throw new Error('Oturum açmanız gerekiyor');
        }

        try {
            const response = await fetch(`${API_URL}/settings`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'Cache-Control': 'no-cache'
                },
                body: JSON.stringify(newSettings)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Ayarlar güncellenemedi');
            }

            setSettings(prev => ({ ...prev, ...newSettings }));
            setLastUpdated(new Date());
            return { success: true };
        } catch (err) {
            console.error('Error updating settings:', err);
            throw err;
        }
    };

    // Force refresh function with cache busting
    const refreshSettings = async () => {
        try {
            setLoading(true);

            const response = await fetch(`${API_URL}/settings?_=${Date.now()}`, {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            });

            if (!response.ok) throw new Error('Failed to refresh settings');

            const data = await response.json();
            setSettings(prev => ({ ...prev, ...data }));
            setLastUpdated(new Date());
            setError(null);
            return data;
        } catch (err) {
            console.error('Error refreshing settings:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchSettings();
    }, []);

    // Periodic refresh (every 60 seconds) - only if no error
    useEffect(() => {
        if (error) return; // Don't auto-refresh if there's an error

        const interval = setInterval(() => {
            refreshSettings();
        }, 60000);

        return () => clearInterval(interval);
    }, [error]);

    const value = {
        settings,
        loading,
        error,
        lastUpdated,
        fetchSettings,
        updateSettings,
        refreshSettings
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};
